import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, ArrowUpDown, ArrowUp, ArrowDown, SlidersHorizontal,
  Columns3, Check, ChevronDown, Users,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Input } from './ui/input';
import { Button } from './ui/button';
import EmptyState from './EmptyState';
import { useRenderProfiler, measureSync } from '../lib/perfMonitor';

/**
 * DataTable gaya beUI — dibina sendiri (inspirasi responsive-data-table beUI Pro):
 * tab animasi, carian, tapis status (multi-pilih), toggle keterlihatan lajur,
 * header boleh-sort, paparan kad di mobile, footer kiraan hasil & lajur.
 *
 * columns: [{ key, header, sortable, sortValue?(row), render(row), className,
 *              align: 'left'|'right'|'center', hideable (default true),
 *              defaultHidden, mobileLabel }]
 */
export default function DataTable({
  columns = [],
  rows = [],
  tabs = [],
  defaultTab = 'semua',
  searchKeys = [],
  searchPlaceholder,
  statusOptions = [],
  onRowClick,
  rowTestId,
  empty,
  tfoot,
  testid = 'datatable',
}) {
  useRenderProfiler('DataTable', { rowCount: rows?.length || 0 });

  const { t } = useTranslation();
  const [tab, setTab] = useState(defaultTab);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ key: null, dir: 1 });
  const [hidden, setHidden] = useState(() => new Set(columns.filter((c) => c.defaultHidden).map((c) => c.key)));
  const [statusSel, setStatusSel] = useState(() => new Set());

  const visibleCols = useMemo(() => columns.filter((c) => !hidden.has(c.key)), [columns, hidden]);

  const filtered = useMemo(() => {
    let out = rows;
    const activeTab = tabs.find((x) => x.key === tab);
    if (activeTab?.filter) out = out.filter(activeTab.filter);
    if (statusSel.size > 0) {
      out = out.filter((r) => statusOptions.some((o) => statusSel.has(o.key) && o.filter(r)));
    }
    const term = q.trim().toLowerCase();
    if (term && searchKeys.length) {
      out = out.filter((r) => searchKeys.some((k) => String(typeof k === 'function' ? k(r) : r[k] ?? '').toLowerCase().includes(term)));
    }
    if (sort.key) {
      const col = columns.find((c) => c.key === sort.key);
      const sv = col?.sortValue || ((r) => r[col.key]);
      out = [...out].sort((a, b) => {
        const va = sv(a), vb = sv(b);
        if (va == null) return 1; if (vb == null) return -1;
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * sort.dir;
        return String(va).localeCompare(String(vb)) * sort.dir;
      });
    }
    return out;
  }, [rows, tab, tabs, q, searchKeys, sort, columns, statusSel, statusOptions]);

  const toggleSort = (key) => {
    setSort((s) => s.key !== key ? { key, dir: 1 } : s.dir === 1 ? { key, dir: -1 } : { key: null, dir: 1 });
  };
  const toggleHidden = (key) => setHidden((h) => { const n = new Set(h); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const toggleStatus = (key) => setStatusSel((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const alignCls = (a) => a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left';

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={testid}>
      {/* toolbar */}
      <div className="print:hidden flex flex-col gap-3 border-b border-border px-4 py-3 lg:flex-row lg:items-center">
        {tabs.length > 0 && (
          <div className="flex items-center gap-1 rounded-full bg-muted p-1 w-fit max-w-full overflow-x-auto">
            {tabs.map((x) => {
              const active = tab === x.key;
              return (
                <button key={x.key} type="button" onClick={() => setTab(x.key)}
                  className={`relative rounded-full px-4 py-1.5 text-sm font-semibold transition-colors whitespace-nowrap shrink-0 ${active ? 'text-white' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid={`${testid}-tab-${x.key}`}>
                  {active && (
                    <motion.span layoutId={`dt-tab-${testid}`} className="absolute inset-0 rounded-full bg-brand"
                      transition={{ type: 'spring', stiffness: 500, damping: 38 }} />
                  )}
                  <span className="relative z-10">{x.label}</span>
                </button>
              );
            })}
          </div>
        )}
        <div className="flex flex-1 items-center gap-2 lg:justify-end">
          {searchKeys.length > 0 && (
            <div className="relative flex-1 lg:max-w-xs">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={searchPlaceholder || t('carian')}
                className="h-10 pl-9" data-testid={`${testid}-search`} />
            </div>
          )}
          {statusOptions.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="h-10 gap-2" data-testid={`${testid}-status-filter`}>
                  <SlidersHorizontal className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('tapisStatus')}</span>
                  {statusSel.size > 0 && (
                    <span className="rounded-full bg-brand px-1.5 text-[11px] font-bold text-white">{statusSel.size}</span>
                  )}
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {statusOptions.map((o) => (
                  <DropdownMenuItem key={o.key} onSelect={(e) => { e.preventDefault(); toggleStatus(o.key); }} className="cursor-pointer">
                    <span className={`mr-2 flex h-4 w-4 items-center justify-center rounded border ${statusSel.has(o.key) ? 'border-brand bg-brand text-white' : 'border-border'}`}>
                      {statusSel.has(o.key) && <Check className="h-3 w-3" />}
                    </span>
                    {o.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="h-10 gap-2" data-testid={`${testid}-columns-toggle`}>
                <Columns3 className="h-4 w-4" />
                <span className="hidden sm:inline">{t('lajur')}</span>
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {columns.filter((c) => c.hideable !== false).map((c) => (
                <DropdownMenuItem key={c.key} onSelect={(e) => { e.preventDefault(); toggleHidden(c.key); }} className="cursor-pointer">
                  <span className={`mr-2 flex h-4 w-4 items-center justify-center rounded border ${!hidden.has(c.key) ? 'border-brand bg-brand text-white' : 'border-border'}`}>
                    {!hidden.has(c.key) && <Check className="h-3 w-3" />}
                  </span>
                  {typeof c.header === 'string' ? c.header : c.key}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* desktop table */}
      <div className="hidden overflow-x-auto md:block print:block">
        <table className="w-full text-left">
          <thead className="sticky top-0 border-b border-border bg-muted">
            <tr className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              {visibleCols.map((c) => (
                <th key={c.key} className={`px-5 py-3 ${alignCls(c.align)} ${c.className || ''}`}>
                  {c.sortable ? (
                    <button type="button" onClick={() => toggleSort(c.key)}
                      className="inline-flex items-center gap-1.5 hover:text-foreground uppercase tracking-wider">
                      {c.header}
                      {sort.key === c.key
                        ? sort.dir === 1 ? <ArrowUp className="h-3.5 w-3.5 text-brand" /> : <ArrowDown className="h-3.5 w-3.5 text-brand" />
                        : <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />}
                    </button>
                  ) : c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            <AnimatePresence initial={false}>
              {filtered.map((r, i) => (
                <motion.tr key={r.id || i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}
                  className={onRowClick ? 'hover:bg-brand-light cursor-pointer transition-colors' : ''}
                  data-testid={rowTestId ? rowTestId(r) : undefined}>
                  {visibleCols.map((c) => (
                    <td key={c.key} className={`px-5 py-3 ${alignCls(c.align)} ${c.className || ''}`}>{c.render(r)}</td>
                  ))}
                </motion.tr>
              ))}
            </AnimatePresence>
          </tbody>
          {tfoot && <tfoot>{tfoot}</tfoot>}
        </table>
      </div>

      {/* mobile cards */}
      <div className="divide-y divide-border md:hidden print:hidden">
        <AnimatePresence initial={false}>
          {filtered.map((r, i) => (
            <motion.div key={r.id || i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={onRowClick ? () => onRowClick(r) : undefined}
              className={`px-4 py-3.5 ${onRowClick ? 'active:bg-brand-light cursor-pointer' : ''}`}
              data-testid={rowTestId ? rowTestId(r) : undefined}>
              <div className="flex flex-col gap-1.5 min-w-0">
                {visibleCols.map((c) => (
                  <div key={c.key} className={`flex items-center justify-between gap-3 ${c.align === 'right' ? 'flex-row-reverse' : ''}`}>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground shrink-0">
                      {c.mobileLabel || (typeof c.header === 'string' ? c.header : c.key)}
                    </span>
                    <span className="text-sm min-w-0 max-w-full overflow-hidden">{c.render(r)}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {filtered.length === 0 && (
        <EmptyState icon={empty?.icon || Users} title={empty?.title || t('tiadaData')} description={empty?.description} />
      )}

      {/* footer */}
      <div className="print:hidden flex items-center justify-between border-t border-border px-5 py-3 text-xs text-muted-foreground">
        <span>{t('hasilCarian', { n: filtered.length })}</span>
        <span>{t('lajurKelihatan', { n: visibleCols.length })}</span>
      </div>
    </div>
  );
}
